import React, { useEffect, useState } from 'react';
import { usePersonnelStore, useAuthStore, getAuthAxios } from '@/store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  UserCog,
  Mail,
  Eye,
  EyeOff,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { useI18n } from '@/components/I18nProvider';

const getRoleBadge = (role) => {
  const roleConfig = {
    super_admin: { label: 'Super Admin', className: 'bg-destructive/10 text-destructive' },
    company_admin: { label: 'Firma Yöneticisi', className: 'bg-primary/10 text-primary' },
    personnel: { label: 'Personel', className: 'bg-status-pending/10 text-status-pending' },
  };
  const config = roleConfig[role] || roleConfig.personnel;
  return <Badge className={config.className}>{config.label}</Badge>;
};

export default function PersonnelPage() {
  const { t } = useI18n();
  const { user } = useAuthStore();
  const { personnel, isLoading, fetchPersonnel, createPersonnel, updatePersonnel, deletePersonnel } = usePersonnelStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPersonnel, setEditingPersonnel] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'personnel',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGeneratingIds, setIsGeneratingIds] = useState(false);

  useEffect(() => {
    fetchPersonnel();
  }, [fetchPersonnel]);

  const hasPersonnelWithoutId = personnel.some(p => !p.short_id && p.role !== 'super_admin');

  const handleGenerateIds = async () => {
    setIsGeneratingIds(true);
    try {
      const api = getAuthAxios();
      const res = await api.post('/personnel/generate-short-ids');
      toast.success(res.data.message);
      fetchPersonnel();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'ID oluşturulamadı');
    }
    setIsGeneratingIds(false);
  };

  const filteredPersonnel = personnel.filter(
    (p) =>
      p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleOpenDialog = (person = null) => {
    if (person) {
      setEditingPersonnel(person);
      setFormData({
        name: person.name,
        email: person.email,
        password: '',
        role: person.role,
      });
    } else {
      setEditingPersonnel(null);
      setFormData({
        name: '',
        email: '',
        password: '',
        role: 'personnel',
      });
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingPersonnel(null);
    setShowPassword(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const data = {
      name: formData.name,
      email: formData.email,
      role: formData.role,
    };

    if (formData.password) {
      if (formData.password.length < 8) { toast.error(t('passwordMinLength')); setIsSubmitting(false); return; }
      if (!/[A-Z]/.test(formData.password)) { toast.error(t('passwordNeedUpper')); setIsSubmitting(false); return; }
      if (!/[a-z]/.test(formData.password)) { toast.error(t('passwordNeedLower')); setIsSubmitting(false); return; }
      if (!/[0-9]/.test(formData.password)) { toast.error(t('passwordNeedDigit')); setIsSubmitting(false); return; }
      data.password = formData.password;
    }

    let result;
    if (editingPersonnel) {
      result = await updatePersonnel(editingPersonnel.id, data);
    } else {
      if (!formData.password) {
        toast.error('Şifre gereklidir');
        setIsSubmitting(false);
        return;
      }
      result = await createPersonnel(data);
    }

    setIsSubmitting(false);

    if (result.success) {
      toast.success(editingPersonnel ? t('personnelUpdated') : t('personnelCreated'));
      handleCloseDialog();
    } else {
      toast.error(result.error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Bu personeli silmek istediğinizden emin misiniz?')) {
      const result = await deletePersonnel(id);
      if (result.success) {
        toast.success(t('personnelDeleted'));
      } else {
        toast.error(result.error);
      }
    }
  };

  const handleToggleActive = async (person) => {
    const result = await updatePersonnel(person.id, { is_active: !person.is_active });
    if (result.success) {
      toast.success(`Personel ${!person.is_active ? 'aktif' : 'pasif'} yapıldı`);
    } else {
      toast.error(result.error);
    }
  };

  const getInitials = (name) => {
    return name
      ?.split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || 'U';
  };

  const isAdmin = user?.role === 'company_admin' || user?.role === 'super_admin';
  const canEditRoles = user?.role === 'super_admin';

  return (
    <div className="space-y-6 animate-fade-in" data-testid="personnel-page">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold">Personeller</h1>
          <p className="mt-1 text-muted-foreground">Ekip üyelerinizi yönetin</p>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            {hasPersonnelWithoutId && (
              <Button variant="outline" onClick={handleGenerateIds} disabled={isGeneratingIds} data-testid="generate-ids-btn">
                {isGeneratingIds ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                ID Oluştur
              </Button>
            )}
            <Button onClick={() => handleOpenDialog()} data-testid="add-personnel-btn">
              <Plus className="mr-2 h-4 w-4" />
              Personel Ekle
            </Button>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid gap-4 grid-cols-3">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-primary/10 p-2">
                <UserCog className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Toplam Personel</p>
                <p className="font-heading text-xl font-bold">{personnel.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-status-paid/10 p-2">
                <UserCog className="h-5 w-5 text-status-paid" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('active')}</p>
                <p className="font-heading text-xl font-bold">{personnel.filter((p) => p.is_active).length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-destructive/10 p-2">
                <UserCog className="h-5 w-5 text-destructive" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('inactive')}</p>
                <p className="font-heading text-xl font-bold">{personnel.filter((p) => !p.is_active).length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Personel ara (isim, e-posta)"
              className="pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="search-personnel"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : (
            <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-20">ID</TableHead>
                  <TableHead>{t('rolePersonnel')}</TableHead>
                  <TableHead className="hidden sm:table-cell">{t('email')}</TableHead>
                  <TableHead>{t('role')}</TableHead>
                  <TableHead>{t('status')}</TableHead>
                  {isAdmin && <TableHead className="text-right">İşlemler</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPersonnel.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isAdmin ? 6 : 5} className="h-24 text-center">
                      {searchTerm ? 'Arama sonucu bulunamadı' : t('noPersonnel')}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredPersonnel.map((person) => (
                    <TableRow key={person.id} data-testid={`personnel-row-${person.id}`}>
                      <TableCell>
                        <Badge variant="outline" className="font-mono text-xs">{person.short_id || '-'}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar>
                            <AvatarFallback className="bg-primary/10 text-primary">
                              {getInitials(person.name)}
                            </AvatarFallback>
                          </Avatar>
                          <span className="font-medium">{person.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">
                        <div className="flex items-center gap-1">
                          <Mail className="h-3 w-3 text-muted-foreground" />
                          {person.email}
                        </div>
                      </TableCell>
                      <TableCell>{getRoleBadge(person.role)}</TableCell>
                      <TableCell>
                        {isAdmin && person.id !== user?.id ? (
                          <Switch
                            checked={person.is_active}
                            onCheckedChange={() => handleToggleActive(person)}
                            data-testid={`toggle-active-${person.id}`}
                          />
                        ) : (
                          <Badge variant={person.is_active ? 'default' : 'secondary'}>
                            {person.is_active ? 'Aktif' : 'Pasif'}
                          </Badge>
                        )}
                      </TableCell>
                      {isAdmin && (
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenDialog(person)}
                            data-testid={`edit-personnel-${person.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          {person.id !== user?.id && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(person.id)}
                              data-testid={`delete-personnel-${person.id}`}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{editingPersonnel ? t('editPersonnel') : 'Yeni Personel Ekle'}</DialogTitle>
            <DialogDescription>
              {editingPersonnel ? 'Personel bilgilerini güncelleyin' : 'Yeni bir personel oluşturun'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Ad Soyad *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Personel adı"
                required
                data-testid="personnel-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">E-posta *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="ornek@firma.com"
                required
                data-testid="personnel-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">
                Şifre {editingPersonnel ? '(değiştirmek için doldurun)' : '*'}
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  required={!editingPersonnel}
                  data-testid="personnel-password-input"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-2 top-1/2 -translate-y-1/2"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="role">{t('role')}</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => setFormData({ ...formData, role: value })}
                disabled={!canEditRoles && formData.role !== 'personnel'}
              >
                <SelectTrigger data-testid="personnel-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="personnel">{t('rolePersonnel')}</SelectItem>
                  <SelectItem value="company_admin">Firma Yöneticisi</SelectItem>
                  {canEditRoles && <SelectItem value="super_admin">Super Admin</SelectItem>}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                İptal
              </Button>
              <Button type="submit" disabled={isSubmitting} data-testid="save-personnel-btn">
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Kaydediliyor...
                  </>
                ) : editingPersonnel ? (
                  t('update')
                ) : (
                  'Oluştur'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
